// Global Constants for ClothedEye Scene Builder
// Centralized configuration to avoid magic numbers and improve maintainability

// Development mode flag
export const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

// GRID CONFIGURATION
export const GRID_CONSTANTS = {
  INCH_TO_PX: 96,
  GRID_INCHES: 31, // Expanded from 21 to 31 inches to accommodate 21% zoom
  GRID_LINES: 32,  // 32 lines for 0-31
  GRID_MAX_PX: 31 * 96, // 2976px
  GLOBAL_ZOOM: 1.0 // Fixed: Removed the 20% scaling that was making stickers too large
};

// ZOOM CONFIGURATION
export const ZOOM_CONFIG = {
  LEVELS: [21, 40, 60, 80, 100, 120, 140, 160, 180, 200],
  DEFAULT: 100,
  MIN: 21,
  MAX: 200,
  STEP: 20
};

// PACK LAYOUT CONFIGURATION
export const PACK_LAYOUT = {
  SPACING: 20, // Space between stickers in a pack
  MAX_COLUMNS: 4, // Maximum columns in pack layout
  MIN_SPACING: 10, // Minimum spacing between stickers
  PACK_PADDING: 15 // Padding around pack groups
};

// POSITIONING CONSTANTS
export const POSITIONING = {
  SNAP_GRID: 8, // Snap to grid every 8 pixels
  MIN_DISTANCE: 5, // Minimum distance between stickers
  BOUNDARY_MARGIN: 10, // Margin from canvas boundaries
  DRAG_THRESHOLD: 3 // Minimum movement to trigger drag
};

// DEFAULT VALUES
export const DEFAULTS = {
  STICKER_SIZE: 'MICRO',
  STICKER_COLOR: 'Black (Matte)',
  BACKGROUND: 'plain',
  ZOOM_LEVEL: 100,
  SHOW_RULER: false,
  SHOW_GRID: true,
  ADMIN_MODE: false
};

// ANIMATION CONSTANTS
export const ANIMATION = {
  DURATION: {
    FAST: 150,
    NORMAL: 300,
    SLOW: 500
  },
  EASING: {
    EASE_IN: 'cubic-bezier(0.4, 0, 1, 1)',
    EASE_OUT: 'cubic-bezier(0, 0, 0.2, 1)',
    EASE_IN_OUT: 'cubic-bezier(0.4, 0, 0.2, 1)'
  }
};

// UI CONSTANTS
export const UI = {
  HEADER_HEIGHT: 80,
  SIDEBAR_WIDTH: 280,
  MODAL_Z_INDEX: 1000,
  TOOLTIP_Z_INDEX: 1100,
  OVERLAY_Z_INDEX: 1200,
  NOTIFICATION_Z_INDEX: 1300
};

// KEYBOARD SHORTCUTS
export const KEYBOARD_SHORTCUTS = {
  CLEAR_SCENE: 'KeyC',
  TOGGLE_RULER: 'KeyR',
  TOGGLE_GRID: 'KeyG',
  ZOOM_IN: 'Equal',
  ZOOM_OUT: 'Minus',
  ZOOM_RESET: 'Digit0',
  ESCAPE: 'Escape',
  ENTER: 'Enter',
  SPACE: 'Space',
  DELETE: 'Delete',
  BACKSPACE: 'Backspace'
};

// TOUCH CONSTANTS
export const TOUCH = {
  TAP_TIMEOUT: 300,
  LONG_PRESS_TIMEOUT: 500,
  SWIPE_THRESHOLD: 50,
  PINCH_THRESHOLD: 10
};

// STORAGE KEYS
export const STORAGE_KEYS = {
  SCENE_DATA: 'clothedeye_scene_data',
  USER_PREFERENCES: 'clothedeye_user_prefs',
  SAVED_SCENES: 'clothedeye_saved_scenes',
  ETSY_AUTH: 'clothedeye_etsy_auth'
};

// API ENDPOINTS
export const API = {
  ETSY_BASE: 'https://api.etsy.com/v3',
  ETSY_OAUTH: 'https://www.etsy.com/oauth',
  CALLBACK_URL: process.env.REACT_APP_ETSY_REDIRECT_URI || 'https://www.clothedeye.com'
};

// VALIDATION CONSTANTS
export const VALIDATION = {
  MAX_STICKERS: 50,
  MAX_SCENE_NAME_LENGTH: 100,
  MAX_DESCRIPTION_LENGTH: 500,
  MIN_STICKER_SIZE: 10,
  MAX_STICKER_SIZE: 500
};

// ERROR MESSAGES
export const ERROR_MESSAGES = {
  STICKER_NOT_FOUND: 'Sticker not found',
  SCENE_LOAD_FAILED: 'Failed to load scene',
  SCENE_SAVE_FAILED: 'Failed to save scene',
  NETWORK_ERROR: 'Network error occurred',
  VALIDATION_ERROR: 'Validation error',
  PERMISSION_DENIED: 'Permission denied',
  GENERIC_ERROR: 'An error occurred'
};

// ACCESSIBILITY CONSTANTS
export const ACCESSIBILITY = {
  FOCUS_OUTLINE: '2px solid #3498db',
  FOCUS_OUTLINE_OFFSET: '2px',
  MIN_TOUCH_TARGET: 44, // Minimum touch target size in pixels
  SKIP_LINK_Z_INDEX: 9999,
  SCREEN_READER_ONLY: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0'
  }
};

// PERFORMANCE CONSTANTS
export const PERFORMANCE = {
  DEBOUNCE_DELAY: 300,
  THROTTLE_DELAY: 16, // 60fps
  VIRTUAL_SCROLL_BUFFER: 5,
  IMAGE_LAZY_LOAD_OFFSET: 100
};

// TIMING CONSTANTS (setTimeout/setInterval delays)
export const TIMING = {
  // Scene persistence
  SCENE_AUTO_SAVE_DELAY: 1000, // Debounce delay for auto-saving scenes
  UNSAVED_CHANGES_RESET: 1200, // Time to reset "unsaved changes" indicator

  // Animations
  SIDEBAR_ANIMATION_DURATION: 300, // Sidebar collapse/expand animation
  TOTAL_ANIMATION_DURATION: 600, // Total calculation animation duration

  // API retry delays
  API_RETRY_VERY_SHORT: 50, // Very short delay for rapid retries
  API_RETRY_SHORT: 100, // Short delay for API retries
  API_RETRY_MEDIUM: 200, // Medium delay for batch operations

  // UI polling
  IMAGE_LOAD_CHECK_INTERVAL: 100, // Check if images loaded
  ASYNC_OPERATION_WAIT: 10, // Short wait for async operations

  // Restore prompt
  RESTORE_SCENE_PROMPT_DELAY: 2000 // Wait before showing restore scene prompt
};

// SCENE BUILDER LAYOUT CONSTANTS
export const SCENE_BUILDER = {
  // Sticker dimensions
  DEFAULT_STICKER_WIDTH: 100,
  DEFAULT_STICKER_HEIGHT: 100,

  // Pack layout spacing
  PACK_BASE_SPACING: 120, // Base spacing between pack stickers (pixels)
  PACK_SPACING_MULTIPLIER: 1.2, // Multiplier for pack spacing

  // Initial positioning
  BASE_X_POSITION: 100, // Starting X position for new stickers
  BASE_Y_POSITION: 200, // Starting Y position for new stickers (below controls)
  POSITION_OFFSET_X: 20, // Horizontal offset between sequential stickers
  POSITION_OFFSET_Y: 20, // Vertical offset between sequential stickers
  POSITION_CYCLE_COUNT: 5, // Number of positions before cycling back

  // Canvas constraints
  MAX_CANVAS_X: 800, // Maximum X position on canvas
  MAX_CANVAS_Y: 600, // Maximum Y position on canvas

  // Sidebar
  DEFAULT_SIDEBAR_WIDTH: 400,
  MIN_SIDEBAR_WIDTH: 300,
  MAX_SIDEBAR_WIDTH: 800,
  COLLAPSED_SIDEBAR_WIDTH: 20
};

// Export all constants as a single object for easy importing
export default {
  IS_DEVELOPMENT,
  GRID_CONSTANTS,
  ZOOM_CONFIG,
  PACK_LAYOUT,
  POSITIONING,
  DEFAULTS,
  ANIMATION,
  UI,
  KEYBOARD_SHORTCUTS,
  TOUCH,
  STORAGE_KEYS,
  API,
  VALIDATION,
  ERROR_MESSAGES,
  ACCESSIBILITY,
  PERFORMANCE,
  TIMING,
  SCENE_BUILDER
}; 